import { inject, Injectable, Injector } from '@angular/core';
import { GlobalService } from './global.service';
import { DropdownService } from './dropdown.service';
import { BindObjectValuePipe } from '../pipes/bind-object-value.pipe';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class FormService {
  gs = inject(GlobalService);
  injector = inject(Injector);

  get gds(): DropdownService {
    return this.injector.get(DropdownService);
  }

  constructor() { }

  /**
   * Generic control comparison validator
   * Compares two form controls and returns validation error based on the comparison operation
   * @param compareToControlName - Name of the control to compare against
   * @param operation - Comparison operation: 'greaterThan', 'lessThan', 'equal', 'notEqual', 'greaterThanOrEqual', 'lessThanOrEqual'
   * @param errorKey - Error key name (e.g., 'passingMarksExceedTotal')
   * @param errorMessage - Custom error message to display
   * @param formGetter - Function that returns the FormGroup (allows form to be available later)
   * @returns ValidatorFn
   */
  compareControls(compareToControlName: string, operation: 'greaterThan' | 'lessThan' | 'equal' | 'notEqual' | 'greaterThanOrEqual' | 'lessThanOrEqual', errorKey: string, errorMessage: string, formGetter: () => any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const currentValue = control.value;
      const form = formGetter();
      const compareToControl = form?.get(compareToControlName);
      const compareToValue = compareToControl?.value;

      if (currentValue == null || compareToValue == null) {
        return null;
      }



      const currentValueNum = Number(currentValue);
      const compareToValueNum = Number(compareToValue);

      let isValid = false;

      if (operation === 'greaterThan') {
        isValid = currentValueNum <= compareToValueNum;
      } else if (operation === 'lessThan') {
        isValid = currentValueNum >= compareToValueNum;
      } else if (operation === 'greaterThanOrEqual') {
        isValid = currentValueNum < compareToValueNum;
      } else if (operation === 'lessThanOrEqual') {
        isValid = currentValueNum > compareToValueNum;
      } else if (operation === 'equal') {
        isValid = currentValueNum !== compareToValueNum;
      } else if (operation === 'notEqual') {
        isValid = currentValueNum === compareToValueNum;
      }

      return isValid ? { [errorKey]: errorMessage } : null;
    };
  }

  async bulk_download(service: any, function_name: string, columns: any, file_name: string = 'bulk_download', params: any) {
    delete params.page;
    let data = await service[function_name]({ ...params, page_size: 9999999 });

    // columns are array of objects { column_name: 'Name', type: 'text', return_as: 'name' }
    let headers = columns.map((col: any) => col.column_name);
    // console.log(headers  , "headers");
    let rows = data.data.map((item: any) => {
      // If column name has active then turn true false to Active Inactive or 
      return columns.map((col: any) => {
        console.log(col, "col");

        if (col.type === 'text') {
          if (col.return_as?.toLowerCase().includes('active')) {
            return item[col.return_as] ? 'Enable' : 'Disable';
          }
          if (col.return_as?.toLowerCase().includes('updatedat')) {
            const raw = item[col.return_as];
            if (raw) {
              const d = new Date(raw);
              const day = String(d.getDate()).padStart(2, '0');
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const year = d.getFullYear();
              return `${day}-${month}-${year}`;
            }
            return '';
          }

          const value = new BindObjectValuePipe().transform(item, col.download_bind || col.return_as);
          return value === '-' ? '' : value;
        } else if (col.type === 'date') {
          const value = new BindObjectValuePipe().transform(item, col.return_as);
          return value && value !== '-' ? new Date(value).toLocaleDateString() : '';
        } else if (col.type === 'number') {
          const value = new BindObjectValuePipe().transform(item, col.return_as);
          return (value !== undefined && value !== null && value !== '-') ? value : 0;
        } else if (col.type === 'link') {
          let value_key = col.download_bind || col.return_as.replace(/_/g, '.');
          let value = new BindObjectValuePipe().transform(item, value_key);

          // Handle array values (e.g., subject_ids: [1, 2, 3])
          if (Array.isArray(value)) {
            return value.map(v => this.get_value_from_dropdown_list(col.linked_list, v)).filter(Boolean).join(', ');
          }
          return this.get_value_from_dropdown_list(col.linked_list, value);
        } else if (col.type === 'boolean_map') {
          const value_key = col.download_bind || col.return_as;
          const value = new BindObjectValuePipe().transform(item, value_key);
          const mapped = col.map_keys?.find((m: any) => m.value === value);

          return mapped ? mapped.key : '';
        }
        return '';
      });
    }
    );
    // return
    // Create CSV content
    let csvContent = headers.map((h: any) => this.csv_safe(h)).join(',') + '\n' + rows.map((row: any) => row.map((cell: any) => this.csv_safe(cell)).join(',')).join('\n');
    // Create a Blob from the CSV content
    let blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    // Create a link element to download the Blob
    let link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = file_name + '.csv';
    // Append the link to the body
    document.body.appendChild(link);
    // Trigger the download
    link.click();
    // Clean up by removing the link and revoking the object URL
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    // this.gs.toastr_shows_function('download completed', 'Success', 'success');
  }

  get_value_from_dropdown_list(list_name: any, value: any) {
    let list = this.gds.dropdown_list[list_name];
    return list?.find((item: any) => item.value === value)?.title || '';
  }

  private csv_safe(value: any): string {
    if (value === null || value === undefined) return '';

    let str = String(value);
    str = str.replace(/"/g, '""');

    if (/[",\n]/.test(str)) { str = `"${str}"` }
    return str;
  }
}
